import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setRuntimeVariable } from '../../actions/runtime';

/**
 * @template {*} U
 * @template {*} T
 *
 * Swap between runtime states.
 *
 * Toggles between the two provided values in runtime.
 * The initial values will be memoized and will not update
 * if programmatically changed after initial call.
 *
 * @param {string} variableName
 * @param {U} defaultState
 * @param {T} elevatedState
 * @return {[U | T, function(): void]}
 */
export default (variableName: any, defaultState = false, elevatedState = true) => {
  const dispatch = useDispatch();

  const values = useRef({
    defaultState: JSON.stringify(defaultState),
    elevatedState: JSON.stringify(elevatedState),
  });

  const value = useSelector(
    useCallback(
      (state: any) =>
        state.runtime.hasOwnProperty(variableName) ? state.runtime[variableName] : defaultState,
      [defaultState, variableName]
    )
  );

  const stringifiedValue = JSON.stringify(value);

  const toggle = useCallback(() => {
    dispatch(
      setRuntimeVariable({
        name: variableName,
        value: JSON.parse(
          stringifiedValue === values.current.defaultState
            ? values.current.elevatedState
            : values.current.defaultState
        ),
      })
    );
  }, [dispatch, variableName, stringifiedValue]);

  return [value, toggle];
};
